import React from 'react';
import { MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { PaymentAttachmentsOutcome } from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { hasAttachmentFailures } from '../../../../functions/CreditorInvoices.Functions';
import { useInvoicesText } from './PaymentInvoices.Bits';
import { ErrorType } from '../../../../models/HttpError';
import './PaymentInvoices.Styles.css';

const detailOf = (error: ErrorType | undefined): string =>
    [error?.ErrorMessage, ...(error?.Errors ?? [])].filter(Boolean).filter((item, index, list) => list.indexOf(item) === index).join(' ') || '—';

/**
 * Resultado de asociar facturas y subir comprobantes después de crear el pago. El pago ya existe:
 * si algo falló, se dice qué y el drawer ofrece "Reintentar".
 */
export const PaymentAttachmentsResult = ({ outcome, working, allocationCount }: {
    outcome: PaymentAttachmentsOutcome | undefined;
    working: boolean;
    /** Facturas que se pidieron asociar. */
    allocationCount: number;
}) => {
    const tr = useInvoicesText();
    if (working && !outcome) {
        return (
            <div className="pinv-stack" data-testid="payment-attachments-working" aria-busy>
                <span><Spinner size="extra-tiny" /> {tr('afterSave.working')}</span>
            </div>
        );
    }
    if (!outcome) return null;
    const failed = hasAttachmentFailures(outcome);
    const warnings = outcome.allocations?.warnings ?? [];
    return (
        <div className="pinv-stack" data-testid="payment-attachments-result">
            <GuideMessageBar intent={failed ? 'warning' : 'success'} layout="multiline">
                <MessageBarBody>
                    <MessageBarTitle>{tr('afterSave.title', { id: outcome.paymentId })}</MessageBarTitle>
                    {failed ? tr('afterSave.failuresText') : tr('afterSave.doneTitle')}
                    <ul className="pinv-results">
                        {outcome.allocations?.status === 'saved' && <li>{tr('afterSave.invoicesSaved', { count: allocationCount })}</li>}
                        {outcome.allocations?.status === 'failed' && (
                            <li data-testid="payment-attachments-invoices-failed">{tr('afterSave.invoicesFailed', { detail: detailOf(outcome.allocations.error) })}</li>
                        )}
                        {outcome.receipts.map((item, index) => (
                            <li key={`${item.fileName}-${index}`} data-testid={`payment-attachments-receipt-${item.status}`}>
                                {item.status === 'uploaded' && tr('afterSave.receiptOk', { name: item.fileName })}
                                {item.status === 'unregistered' && tr('afterSave.receiptUnregistered', { name: item.fileName, detail: detailOf(item.error) })}
                                {item.status === 'failed' && tr('afterSave.receiptFailed', { name: item.fileName, detail: detailOf(item.error) })}
                            </li>
                        ))}
                    </ul>
                </MessageBarBody>
            </GuideMessageBar>
            {warnings.length > 0 && (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="payment-attachments-warnings">
                    <MessageBarBody>
                        <MessageBarTitle>{tr('afterSave.warningsTitle')}</MessageBarTitle>
                        <ul className="pinv-results">{warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {working && <span><Spinner size="extra-tiny" /> {tr('afterSave.working')}</span>}
        </div>
    );
};

export default PaymentAttachmentsResult;
